import { Component, ElementRef, EventEmitter, HostListener, Input, Output, Type, ViewChild, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { AutoComplete } from '@openng/optimus-ui/autocomplete';
import { Button } from '@openng/optimus-ui/button';
import { CascadeSelect } from '@openng/optimus-ui/cascadeselect';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { DatePicker } from "@openng/optimus-ui/datepicker";
import { Fieldset } from '@openng/optimus-ui/fieldset';
import { InputMask } from '@openng/optimus-ui/inputmask';
import { InputNumber } from '@openng/optimus-ui/inputnumber';
import { Listbox } from '@openng/optimus-ui/listbox';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { Password } from '@openng/optimus-ui/password';
import { RadioButton } from '@openng/optimus-ui/radiobutton';
import { Rating } from '@openng/optimus-ui/rating';
import { Select } from "@openng/optimus-ui/select";
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { MultiWordFilter } from "src/modules/utils/misc/multi-word-filter";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { InsertionPointDirective } from '../../directives/insertion-point.directive';
import { TableData } from '../../models/table-data.model';
import { TableRowEvent } from "../../models/table-row-event.model";
import { TableRow } from '../../models/table-row.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { ColorEditorComponent } from '../color-editor/color-editor.component';
import { SmFileUploadComponent } from '../sm-file-upload/sm-file-upload.component';
import { SmImageUploadComponent } from '../sm-image-upload/sm-image-upload.component';
import { SmTableComponent } from '../sm-table/sm-table.component';
import { FrontendFieldValueChangedEvent } from '../../models/frontend-field-value-changed-event.model';
import { StringListEditorComponent } from '../string-list-editor/string-list-editor.component';
import { SmTabViewComponent } from '../sm-tab-view/sm-tab-view.component';
import { TableCellEvent } from '../../models/table-cell-event.model';

@Component({
    selector: 'app-sm-form-field-internal',
    templateUrl: './sm-form-field-internal.component.html',
    styleUrls: ['./sm-form-field-internal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SmFormFieldInternalComponent {

    _FrontendFieldType = FrontendFieldType;
    _Strings = Strings;
    _Utils = Utils;

    app = inject(MainAppService);
    elementRef = inject(ElementRef);

    identity = (option): any => option; 

    errorMessage = "";
    fixedPos = false;
    tableData: TableData;
    tableDataIsForValue: any[];

    @Input()
    form: FrontendForm;
    @Input()
    fluentWidth: string = null;
    @Input()
    fullHeight = false;

    mainRadioButtonField: FrontendField;

    @Output()
    onClick = new EventEmitter<FrontendField>();

    private _field: FrontendField = null;
    private tableValidationState: string;
    private valueBeforeEdit: any;
    internalTimeValue: string;

    autoCompleteSuggestions = [];

    pageChildren: FrontendField[][][] = [];

    _mustRefresh = this.mustRefresh.bind(this);

    @ViewChild(InsertionPointDirective, {static: true}) insertionPoint: InsertionPointDirective;

    @ViewChild("componentFieldSet") componentFieldSet: Fieldset;
    @ViewChild("componentInput") componentInput: any;
    @ViewChild("componentPassword") componentPassword: Password;
    @ViewChild("componentInputNumber") componentInputNumber: InputNumber;
    @ViewChild("componentRating") componentRating: Rating;
    @ViewChild("componentInputMask") componentInputMask: InputMask;
    @ViewChild("componentMultiSelect") componentMultiSelect: MultiSelect;
    @ViewChild("componentAutoComplete") componentAutoComplete: AutoComplete;
    @ViewChild("componentSelect") componentDropdown: Select;
    @ViewChild("componentSelectButton", { read: ElementRef }) componentSelectButton: ElementRef;
    @ViewChild("componentCascadeSelect") componentCascadeSelect: CascadeSelect;
    @ViewChild("componentStringListEditor") componentStringListEditor: StringListEditorComponent;
    @ViewChild("componentListBox") componentListBox: Listbox;
    @ViewChild("componentTextArea") componentTextArea: any;
    @ViewChild("componentDatePicker") componentDatePicker: DatePicker;
    private dateOverwriteInput: HTMLInputElement = null;
    @ViewChild("componentCheckBox") componentCheckBox: Checkbox;
    @ViewChild("componentRadioButton") componentRadioButton: RadioButton;
    @ViewChild("componentColorEditor") componentColorEditor: ColorEditorComponent;
    @ViewChild("componentButton") componentButton: Button;
    @ViewChild("componentButtonAsLink") componentButtonAsLink: any;
    @ViewChild("componentFileUpload") componentFileUpload: SmFileUploadComponent;
    @ViewChild("componentImageUpload") componentImageUpload: SmImageUploadComponent;
    @ViewChild("componentTable") componentTable: SmTableComponent;
    @ViewChild("componentTabs") componentTabs: SmTabViewComponent;

    _addTableRow = this.addTableRow.bind(this);
    _setTableRows = this.setTableRows.bind(this);
    _getTableCellStyleClass = this.getTableCellStyleClass.bind(this);
    private readonly filteredDropdownTabHandler = (event: KeyboardEvent): void => {
        let dropdown = this.componentDropdown ?? this.componentMultiSelect;
        if (event.key != "Tab" || !dropdown?.overlayVisible) {
            return;
        }
        let target = event.target as Node;
        let control = dropdown.el.nativeElement as HTMLElement;
        let overlay = dropdown.overlayViewChild?.overlayViewChild?.nativeElement as HTMLElement;
        if (!control.contains(target) && !overlay?.contains(target)) {
            return;
        }

        let focusInput = dropdown.focusInputViewChild?.nativeElement as HTMLElement;
        let direction = event.shiftKey ? -1 : 1;
        let focusable = Array.from(control.ownerDocument.querySelectorAll<HTMLElement>("input, select, textarea, button, a[href], [tabindex]"))
            .filter(element => element.tabIndex >= 0 && !element.hasAttribute("disabled") && element.getClientRects().length > 0 && !overlay?.contains(element) && (!control.contains(element) || element == focusInput));
        let index = focusable.indexOf(focusInput);
        let next = index >= 0 ? focusable[index + direction] : null;

        event.preventDefault();
        event.stopPropagation();
        dropdown.hide();
        GuiUtils.angularTimer(() => (next ?? focusInput)?.focus());
    };

    ngAfterViewInit(): void {
        let ne = this.componentAutoComplete?.el?.nativeElement;
        if (ne != null) {
            //Bei einem AutoComplete wollen wir nicht, dass die Tab Taste zum Dropdown-Pfeil-Button springt, sondern direkt zum nächsten Control
            ne.querySelector('.p-autocomplete-dropdown')?.setAttribute('tabindex', '-1');
        }
        ne = this.componentDropdown?.el?.nativeElement;
        if (ne != null) {
            //Andernfalls wird das Drücken von Tab von p-select verschluckt und das Tabbing-Verhalten ist sehr merkwürdig
            ne.addEventListener('keydown', (e: KeyboardEvent) => {
                this._field.tableCellEditor?.handleKeyboardEvent(e);
            }, { capture: true });
        }
        this.field.guiComponent = this.getGuiComponent();
        this.attachDateOverwrite();
    }

    ngAfterViewChecked(): void {
        this.attachDateOverwrite();
    }

    ngOnDestroy(): void {
        this.filteredDropdownHidden();
    }

    filteredDropdownShown(): void {
        if (this.field.dropdownCanFilter && this.field.tableCellEditor == null) {
            (this.elementRef.nativeElement as HTMLElement).ownerDocument.addEventListener("keydown", this.filteredDropdownTabHandler, true);
        }
    }

    filteredDropdownHidden(): void {
        (this.elementRef.nativeElement as HTMLElement).ownerDocument.removeEventListener("keydown", this.filteredDropdownTabHandler, true);
    }

    ngDoCheck(_): void {
        let errors = this.field?.getErrors(true, true, false) ?? [];
        this.errorMessage = Utils.isNoe(errors) ? null : Arrays.itemsToString(errors, "\n", error => error.message);
        this.fixedPos = this.field?.isFixedPosition();
        if (this.field?.type == FrontendFieldType.table) {
            let tableValidationState = Objects.getPropertyNames(this.field.tableInvalidCells).join(";") + "|" + errors.filter(error => error.subItem != null).map(error => error.subItem).join(";");
            if (tableValidationState != this.tableValidationState) {
                this.tableValidationState = tableValidationState;
                this.field.tableMustRefresh = true;
            }
            if (this.tableData == null || this.tableDataIsForValue != this.field.value ||
                this.tableDataIsForValue != null && this._field.value != null && this.tableDataIsForValue.length != this._field.value.length || this.tableData.columns?.length != this.field.tableColumns?.length) {
                this.tableData = this.field.value != null ? new TableData(this.field.tableColumns, (this.field.value as any[]).map(row => new TableRow("", row, row as OrdinaryObject))) : null;
                this.tableDataIsForValue = this.field.value;
            }
        }
        else {
            this.tableData = null;
            this.tableValidationState = null;
        }
        if (this.field.tableSelectedRow != this.field.tableSelectedRowRequest) {
            this.tableSetSelectedRowIndex(this.field.tableSelectedRowRequest);
        }
        if (this.field.type == FrontendFieldType.radioButton) {
            this.mainRadioButtonField = this.form.getAllFields().find(f => f.radioButtonGroup == this.field.radioButtonGroup && f.radioButtonValue == 0);    
        }   
        if (this.field?.type == FrontendFieldType.time) {
            this.internalTimeValue = Dates.toTimeString(this.field.value as number);
        }

        if (this.field.requestFocus) {
            this.field.requestFocus = false;
            this.focus();
        }

    }

    @Input()
    set field(value: FrontendField) {
        this._field = value;

        if (this.field.type == FrontendFieldType.custom) {
            this._field.component = this.app.createComponent(this.insertionPoint, this._field.componentType as Type<any>);
            this._field.component.value = this._field.value;
            this._field.component.tableCellEditor = this._field.tableCellEditor;
           /* this._field.component.valueChange.subscribe(newValue => {
                this.valueChanged(true);
            });
          */  this._field.component.setContext(this._field.context);
        }
        this.pageChildren = this.field?.type == FrontendFieldType.pageControl ? this.field.children.map(page => page.getGroupedChildren()) : [];
    }

    get field(): FrontendField {
        return this._field;
    }

    @HostListener("mousedown", ["$event"])
    controlMouseDown(event: MouseEvent): void {
        if (this._field?.userDefinedData?.openAdvancedSearchOnMouseDown === true && this._field.onClick != null) {
            this._field.onClick(event);
        }
    }

    buttonClick(event: MouseEvent): void {
        //Aus unbekannten Gründen wird dieser Eventhandler auch aufgerufen, wenn in einem beliebigen Formfeld Enter gedrückt wird.
        //Dann sind screenX und screenY = 0. Solche "Klicks" werden ignoriert.
        if (event.screenX === 0 && event.screenY === 0) {
            return;
        }
        if (this._field.onClick != null) {
            this._field.onClick(event);
        }
    }

    keyPress(event: KeyboardEvent): void {
        if (this.form.submitOnEnter && event.key == "Enter") {
            event.preventDefault();
            let button = this.form.getSubmitButton();
            if (button?.onClick != null) {
                button?.onClick(null);
                return;
            }
        }

        if (this._field.onKeyPress != null) {
            this._field.onKeyPress(event);
        }
    }

    mustRefresh(): boolean {
        if (this._field == null) {
            return false;
        }
        let result = this._field.tableMustRefresh;
        this._field.tableMustRefresh = false;
        return result;
    }

    getTableCellStyleClass(event: TableCellEvent): string {
        return this._field.getCellStyleClass(event.value, event.rowIndex, event.columnIndex);
    }

    valueChanged(byUser = true): void {
        let f = this.mainRadioButtonField ?? this._field;

        if (f.type == FrontendFieldType.dropdown && f.tableCellEditor != null) {
            f.tableCellEditor.toNextCell();
        }

        if (f.type == FrontendFieldType.bool && f.checkBoxTriState) {
            f.value = f.value === null ? true : f.value === true ? false : null;
        }

        if (f.value != null && (f.type == FrontendFieldType.decimal || f.type == FrontendFieldType.int) && this.field.allowOnlySpinnerStepValues) {
            let corrected = Utils.roundToDecimal(f.value as number, f.spinnerStep);
            if (f.value != corrected) {
                GuiUtils.angularTimer(() => {
                    f.value = corrected;
                });
            }
        }

        //Diese Zeile benötigen wir, weil das AutoComplete beim Selektieren eines Items zuerst ein ValueChanged mit Wert null abfeuert, und danach
        //ein zweites mit dem ausgewählten Wert. Diesen temporären Zwischenstand wollen wir nicht weitergeben
        if (f.value == null && f.type == FrontendFieldType.dropdown && f.dropdownAutoComplete && this.componentAutoComplete?.overlayVisible) {
            return;
        }

        if (f?.type == FrontendFieldType.time) {
            f.value = Dates.fromTimeString(this.internalTimeValue);
        }
        f.valueWasChanged(new FrontendFieldValueChangedEvent(f.id, f.value, null, false, byUser));
    }

    addTableRow(): void {
        if (this._field.onBeforeAddRow != null) {
            this._field.onBeforeAddRow();
        }
        this._field.value = [...(this._field.value as object[]), this._field.generateEmptyTableItem()];
        this.valueChanged();
    }

    removeTableRow(evt: TableRowEvent): void {
        this._field.value = Arrays.withoutIndex(this._field.value as any[], evt.rowIndex);
        this.valueChanged();
    }

    setTableRows(rows: TableRow[]): void {
        this._field.value = rows.map(row => row.values);
        this.valueChanged();
    }

    deleteTableRow(rowIndex: number): void {
        (this._field.value as object[]).splice(rowIndex, 1);
        this.valueChanged();
        //Das wird ben?tigt, um die Change Detection zu triggern. Sonst stimmen Breiten von Header und Zeilen nicht überein
        this._field.value = [...(this._field.value as object[])];
    }

    async editTableRow(rowIndex: number): Promise<void> {
        let form = new FrontendForm(this._field.tableColumns.map(col => col.toFrontendField()));
        form.fill((this._field.value as object[])[rowIndex]);
        await this.app.dialog.form(form, "Zeile editieren");
    }

    tableSelectRow(row: TableRow): void {
        this.field.tableSelectedRow = this.tableData.rows.findIndex(row2 => row2 == row);
        this.field.tableSelectedRowRequest = this.field.tableSelectedRow;
    }

    tableSetSelectedRowIndex(index: number): void {
        this.field.tableSelectedRow = index;
        this.field.tableSelectedRowRequest = index;
        if (this.componentTable != null) {
            this.componentTable.setSelectedRowIndex(index);
        }
    }

    tableEditInit(): void {
        this.valueBeforeEdit = Objects.cloneDeep(this.field.value);
    }

    tableEditComplete(): void {
        if (!Objects.equalsDeep(this.valueBeforeEdit, this.field.value)) {
            this._field.valueWasChanged(new FrontendFieldValueChangedEvent(this.field.id, this.field.value, this.valueBeforeEdit, true, true));
        }
    }

    focus(): void {
        let c = this.getGuiComponent();
        if (c != null) {
            GuiUtils.angularTimer(() => {
                c.nativeElement?.focus();
                //für Multiselekt;
                c.containerViewChild?.nativeElement?.querySelector('input')?.focus();
                // Der Datepicker hat weder nativeElement noch containerViewChild, sein Eingabefeld liegt in inputfieldViewChild
                c.inputfieldViewChild?.nativeElement?.focus();
            });
        }
    }

    thisComponentAsTableEditorInited(): void {
        this.componentDropdown?.show(true);
    }

    async autoComplete(event: any): Promise<void> {
        let q = event.query as string;
        if (this._field.onAutoComplete != null) {
            this.autoCompleteSuggestions = await this._field.onAutoComplete(q);
        }
        else {
            this.autoCompleteSuggestions = q.length >= this._field.dropdownAutoCompleteMinLength ? this.field.listItems.filter(li => li.label != null && MultiWordFilter.includes(li.label, q)) : [];
            if (this._field.dropdownAutoCompleteMaxItems != null) {
                this.autoCompleteSuggestions = Arrays.subList(this.autoCompleteSuggestions, 0, this._field.dropdownAutoCompleteMaxItems);
            }
        }
    }

    colorChanged(color: string): void {
        this.field.value = Utils.toColorNumber(color);
    }

    getStyle(style: OrdinaryObject): OrdinaryObject {
        return  { ... style, ...this.field.style };
    }
    getTextAreaStyle(): OrdinaryObject {
        return this.getStyle({
            width: this.fluentWidth ?? '100%',
            height: this.field.textAreaAutoHeight ? undefined : this.field.fixedHeight || '200px',
            minHeight: this.field.textAreaMinHeight,
            maxHeight: this.field.textAreaMaxHeight,
            overflowY: this.field.textAreaAutoHeight ? 'auto' : undefined
        });
    }

    getGuiComponent(): any {
        return this.componentFieldSet
            ?? this.componentInput
            ?? this.componentPassword
            ?? this.componentInputNumber
            ?? this.componentRating
            ?? this.componentInputMask
            ?? this.componentMultiSelect
            ?? this.componentAutoComplete
            ?? this.componentDropdown
            ?? this.componentSelectButton
            ?? this.componentCascadeSelect
            ?? this.componentStringListEditor
            ?? this.componentListBox
            ?? this.componentTextArea
            ?? this.componentDatePicker
            ?? this.componentCheckBox
            ?? this.componentRadioButton
            ?? this.componentColorEditor
            ?? this.componentButton
            ?? this.componentButtonAsLink
            ?? this.componentFileUpload
            ?? this.componentImageUpload
            ?? this.componentTable
            ?? this.componentTabs
            ?? null;
    }

    // Nach einer Auswahl im Kalender bliebe der Fokus im geschlossenen Popup; in Tabellen kehrt er in die Zelle zurück, sonst ins Eingabefeld
    datePickerSelected(): void {
        if (this.field.tableCellEditor != null) {
            this.field.tableCellEditor.close();
            return;
        }
        GuiUtils.angularTimer(() => (this.componentDatePicker?.inputfieldViewChild?.nativeElement as HTMLInputElement)?.focus());
    }

    private attachDateOverwrite(): void {
        let input = this.componentDatePicker?.inputfieldViewChild?.nativeElement as HTMLInputElement;
        if (input == null || input == this.dateOverwriteInput) {
            return;
        }
        this.dateOverwriteInput = input;
        input.addEventListener("beforeinput", (event: InputEvent) => this.dateOverwrite(event, input));
    }

    private getDateTemplate(): string {
        if (this.field.type == FrontendFieldType.dateTime) {
            return "00.00.0000 00:00";
        }
        if (this.field.datePickerYear) {
            return "0000";
        }
        return this.field.datePickerMonth ? "00.0000" : "00.00.0000";
    }

    // Datumsfelder haben einen festen Aufbau: Ziffern überschreiben das Zeichen unter dem Cursor, Trennzeichen werden übersprungen bzw. am Ende ergänzt
    private dateOverwrite(event: InputEvent, input: HTMLInputElement): void {
        if (event.inputType == "insertFromPaste") {
            event.preventDefault();
            input.value = (event.dataTransfer?.getData("text/plain") ?? event.data ?? "").trim();
            input.dispatchEvent(new Event("input", { bubbles: true }));
            return;
        }
        if (event.inputType != "insertText" || event.data?.length != 1 || input.selectionStart != input.selectionEnd) {
            return;
        }
        event.preventDefault();
        let template = this.getDateTemplate();
        let value = input.value;
        let pos = input.selectionStart ?? value.length;
        let isSeparator = (index: number): boolean => index < value.length ? !/\d/.test(value[index]) : index < template.length && template[index] != "0";
        if (!/\d/.test(event.data)) {
            if (pos < value.length && value[pos] == event.data) {
                input.setSelectionRange(pos + 1, pos + 1);
            }
            else if (pos == value.length && isSeparator(pos) && template[pos] == event.data) {
                this.setDateInputValue(input, value + event.data, pos + 1);
            }
            return;
        }
        while (isSeparator(pos)) {
            if (pos >= value.length) {
                value += template[pos];
            }
            pos++;
        }
        if (pos >= template.length && pos >= value.length) {
            return;
        }
        value = pos < value.length ? value.substring(0, pos) + event.data + value.substring(pos + 1) : value + event.data;
        pos++;
        while (pos < value.length && !/\d/.test(value[pos])) {
            pos++;
        }
        this.setDateInputValue(input, value, pos);
    }

    private setDateInputValue(input: HTMLInputElement, value: string, cursor: number): void {
        input.value = value;
        // Der Datepicker übernimmt den Wert nur über sein input-Ereignis; der Cursor wird danach gesetzt, falls er das Feld dabei neu beschreibt
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.setSelectionRange(cursor, cursor);
    }

    get valueWrapperDateTime(): any {
        return this.field.value;
    }

    set valueWrapperDateTime(value: any) {
        let oldValue = this.field.value;
        let changed = this.field.value != value;
        this.field.value = value;
        if (changed) {
            this.field.valueWasChanged(new FrontendFieldValueChangedEvent(this.field.id, this.field.value, oldValue, true, true));
        }
    }

    dateTimeBlur(event: Event): void {
        let value = (event.target as HTMLInputElement).value.trim();
        if (!/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(value)) {
            return;
        }
        try {
            let date = this.componentDatePicker.parseDate(value, "dd.mm.yy");
            this.componentDatePicker.updateModel(date);
        }
        catch {
            // Ungültige Datumsangaben bleiben beim bisherigen Eingabeverhalten.
        }
    }

}
